/**
 * A page's code, fetched when first opened, recovering from the two ways that fails in practice.
 *
 * - **A blip:** the portal restarting for a deploy, or the network dropping a request, for a moment.
 *   One more try a second later gets it.
 * - **A new build:** a tab open across a deploy asks for files by the old build's names. Only a
 *   reload, which fetches the new build's index, finds them; it happens at most once a minute, so a
 *   portal that is really down shows its error rather than reloading forever.
 *
 * Without this, `React.lazy` keeps the failed import, and the error page's Retry could never work.
 */
const RELOADED_AT = 'portal-reloaded-for-update'

export async function loadPage<T>(
  load: () => Promise<T>,
  wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms)),
): Promise<T> {
  try {
    return await load()
  } catch {
    await wait(1000)
  }
  try {
    return await load()
  } catch (failure) {
    let last: number
    try {
      last = Number(sessionStorage.getItem(RELOADED_AT) ?? 0)
      if (Date.now() - last > 60_000) sessionStorage.setItem(RELOADED_AT, String(Date.now()))
    } catch {
      // Storage refused: nothing could stop a reload loop, so show the error instead.
      throw failure
    }
    if (Date.now() - last > 60_000) {
      window.location.reload()
      // The reload replaces the page; until then, show the loading state rather than an error.
      return new Promise<T>(() => {})
    }
    throw failure
  }
}
